/*
 * Elexa core (loaded deferred on every page).
 *
 * window.Elexa
 *   .config            server bootstrap (snippets/elexa-bootstrap.liquid)
 *   .EVENTS / .emit / .on            contract events on document
 *   .money(cents, format?) / .moneyWithCurrency(cents) / .savePercent(compare, price)
 *   .time              store-timezone helpers
 *   .countdown         countdown maths + <elexa-countdown>
 *   .delivery          delivery-date utility + <elexa-delivery-date>
 *   <elexa-carousel>   scroll-snap carousel (arrows, dots, progress, counter, autoplay, loop, RTL)
 *   <elexa-accordion>  details/summary accordion (single-open, animated)
 *   .cart              cart pipeline stub: add / change / update / refresh / applyDiscount / get / open
 *                      with extension points (hooks, sections, renderers) for group B
 *
 * Docs: SPECS/ELEXA_DEV.md. No dependencies; uses Dawn's publish/PUB_SUB_EVENTS when present.
 */
(() => {
  if (window.Elexa && window.Elexa.coreLoaded) return;
  const Elexa = (window.Elexa = window.Elexa || {});
  Elexa.coreLoaded = true;
  Elexa.version = '1.0.0';
  const config = (Elexa.config = Elexa.config || {});
  const strings = config.strings || {};
  const routes = config.routes || {};

  /* ------------------------------------------------------------------ *
   * Events and small utilities
   * ------------------------------------------------------------------ */
  Elexa.EVENTS = Object.freeze({
    variantChange: 'elexa:variant-change',
    offerChange: 'elexa:offer-change',
    cartUpdated: 'elexa:cart-updated',
    cartError: 'elexa:cart-error',
    cartRefreshRequest: 'elexa:cart-refresh-request',
    carouselChange: 'elexa:carousel-change',
    countdownExpired: 'elexa:countdown-expired',
  });

  /** Dispatch a CustomEvent (bubbles) on `target` (default document). */
  Elexa.emit = (name, detail = {}, target = document) =>
    target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  /** Listen on `target` (default document). Returns an unsubscribe function. */
  Elexa.on = (name, handler, target = document) => {
    target.addEventListener(name, handler);
    return () => target.removeEventListener(name, handler);
  };

  Elexa.reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  Elexa.isRTL = (el) => getComputedStyle(el || document.documentElement).direction === 'rtl';
  Elexa.isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode) || config.designMode === true;

  /** Replace [key] placeholders: Elexa.format('Go to slide [index]', { index: 2 }). */
  Elexa.format = (str, vars = {}) =>
    String(str || '').replace(/\[(\w+)\]/g, (m, key) => (key in vars ? vars[key] : m));

  Elexa.debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  /* ------------------------------------------------------------------ *
   * Motion helpers: CSS easing curves in JS and CSS time values
   * ------------------------------------------------------------------ */
  const EASE_KEYWORDS = {
    linear: [0, 0, 1, 1],
    ease: [0.25, 0.1, 0.25, 1],
    'ease-in': [0.42, 0, 1, 1],
    'ease-out': [0, 0, 0.58, 1],
    'ease-in-out': [0.42, 0, 0.58, 1],
  };
  function cubicBezier(x1, y1, x2, y2) {
    const sample = (a1, a2, t) => ((1 - 3 * a2 + 3 * a1) * t + (3 * a2 - 6 * a1)) * t * t + 3 * a1 * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0;
      let hi = 1;
      let t = x;
      for (let i = 0; i < 24; i++) {
        const v = sample(x1, x2, t);
        if (Math.abs(v - x) < 1e-5) break;
        if (v < x) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
      return sample(y1, y2, t);
    };
  }
  /** Easing function from a CSS value: keyword or cubic-bezier(a, b, c, d). Unknown values give `ease`. */
  Elexa.easing = (value = 'ease') => {
    const v = String(value || 'ease').trim();
    const m = v.match(/^cubic-bezier\(\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\)$/);
    const p = m ? m.slice(1).map(Number) : EASE_KEYWORDS[v] || EASE_KEYWORDS.ease;
    return cubicBezier(p[0], p[1], p[2], p[3]);
  };
  /** Milliseconds from a CSS time ('500ms', '0.5s', '500'); `fallback` when blank or invalid. */
  Elexa.toMs = (value, fallback = 0) => {
    const v = String(value || '').trim();
    if (!v) return fallback;
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return fallback;
    return /[^m]s$/.test(v) ? n * 1000 : n;
  };

  /* ------------------------------------------------------------------ *
   * Money
   * ------------------------------------------------------------------ */
  const MONEY_FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };

  function toCents(value) {
    if (typeof value === 'string') value = value.replace('.', '').trim();
    const n = typeof value === 'number' ? value : parseInt(value, 10);
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  function groupThousands(intString, sep) {
    return intString.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  /**
   * Format an amount in cents with the shop money format (all Shopify placeholders).
   * Rounding is half-up. Empty or non-numeric input formats as 0.
   */
  Elexa.money = (cents, format) => {
    const fmt = format || config.moneyFormat || '{{amount}}';
    const c = toCents(cents);
    const negative = c < 0;
    const abs = Math.abs(c);
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match && MONEY_FORMATS[match[1]] ? match[1] : 'amount';
    const [precision, thousands, decimal] = MONEY_FORMATS[key];
    let out;
    if (precision === 0) {
      out = groupThousands(String(Math.floor((abs + 50) / 100)), thousands);
    } else {
      const whole = Math.floor(abs / 100);
      const frac = String(abs % 100).padStart(2, '0');
      out = groupThousands(String(whole), thousands) + decimal + frac;
    }
    if (negative) out = '-' + out;
    return match ? fmt.replace(match[0], out) : out;
  };

  Elexa.moneyWithCurrency = (cents) => Elexa.money(cents, config.moneyWithCurrencyFormat);

  /** Whole-number save percentage (contract: round to nearest). */
  Elexa.savePercent = (compareCents, priceCents) => {
    const compare = toCents(compareCents);
    const price = toCents(priceCents);
    if (!compare || compare <= price) return 0;
    return Math.round(((compare - price) * 100) / compare);
  };

  /* ------------------------------------------------------------------ *
   * Store time (dates typed by the merchant are in the store timezone)
   * ------------------------------------------------------------------ */
  function parseOffset(str) {
    const m = String(str || '+0000').match(/([+-])(\d{2}):?(\d{2})/);
    if (!m) return 0;
    const minutes = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    return m[1] === '-' ? -minutes : minutes;
  }

  const offsetMinutes = parseOffset(config.utcOffset);
  const offsetMs = offsetMinutes * 60000;

  Elexa.time = {
    offsetMinutes,
    /** Epoch ms now. */
    now: () => Date.now(),
    /** A Date whose UTC fields show the store's wall clock. Read it with getUTC*(). */
    storeNow: () => new Date(Date.now() + offsetMs),
    /** Wall-clock parts of an epoch-ms instant in store time. weekday: 0 = Sunday. */
    storeParts(ms = Date.now()) {
      const d = new Date(ms + offsetMs);
      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
        hours: d.getUTCHours(),
        minutes: d.getUTCMinutes(),
        seconds: d.getUTCSeconds(),
        weekday: d.getUTCDay(),
      };
    },
    /** Epoch ms of a store wall-clock time. */
    fromStore(year, month, day, hours = 0, minutes = 0, seconds = 0) {
      return Date.UTC(year, month - 1, day, hours, minutes, seconds) - offsetMs;
    },
    /** Parse 'YYYY-MM-DD' + optional 'HH:MM' in store time. Returns epoch ms or NaN. */
    parseStoreDate(date, time = '00:00') {
      const d = String(date || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      const t = String(time || '00:00').trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (!d || !t) return NaN;
      return Elexa.time.fromStore(+d[1], +d[2], +d[3], +t[1], +t[2], +(t[3] || 0));
    },
    /**
     * Parse any deadline: epoch ms number, ISO string with offset, or 'YYYY-MM-DD HH:MM'
     * (read in store time). Returns epoch ms or NaN.
     */
    parseDeadline(value) {
      if (typeof value === 'number') return value;
      const s = String(value || '').trim();
      if (/^\d{10,}$/.test(s)) return s.length <= 10 ? +s * 1000 : +s;
      if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
        const ms = Date.parse(s);
        return Number.isNaN(ms) ? NaN : ms;
      }
      const [date, time] = s.split(/[ T]/);
      return Elexa.time.parseStoreDate(date, time || '00:00');
    },
    /** Next occurrence of 'HH:MM' in store time (today if still ahead, else tomorrow). */
    nextDaily(hhmm) {
      const t = String(hhmm || '').trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!t) return NaN;
      const p = Elexa.time.storeParts();
      let ms = Elexa.time.fromStore(p.year, p.month, p.day, +t[1], +t[2], 0);
      if (ms <= Date.now()) ms += 86400000;
      return ms;
    },
  };

  /* ------------------------------------------------------------------ *
   * Countdown engine
   * ------------------------------------------------------------------ */
  const pad2 = (n) => String(n).padStart(2, '0');

  Elexa.countdown = {
    /** Split remaining ms. With showDays false, hours keep counting past 24. */
    split(remainingMs, showDays = true) {
      const total = Math.max(0, remainingMs);
      const days = showDays ? Math.floor(total / 86400000) : 0;
      const hours = showDays ? Math.floor((total % 86400000) / 3600000) : Math.floor(total / 3600000);
      const minutes = Math.floor((total % 3600000) / 60000);
      const seconds = Math.floor((total % 60000) / 1000);
      return { total, days, hours, minutes, seconds };
    },
    /** Human label, minute resolution: "2 days 4 hours 5 minutes". */
    label(remainingMs) {
      const p = Elexa.countdown.split(remainingMs, true);
      const parts = [];
      if (p.days) parts.push(`${p.days} ${(strings.days || 'days').toLowerCase()}`);
      if (p.hours) parts.push(`${p.hours} ${(strings.hours || 'hours').toLowerCase()}`);
      parts.push(`${p.minutes} ${(strings.minutes || 'minutes').toLowerCase()}`);
      return Elexa.format(strings.timeLeft || 'Time left: [time]', { time: parts.join(' ') });
    },
  };

  // One shared 1-second ticker for every countdown; paused while the tab is hidden.
  const ticker = {
    items: new Set(),
    timer: null,
    add(item) {
      this.items.add(item);
      this.start();
    },
    remove(item) {
      this.items.delete(item);
      if (!this.items.size) this.stop();
    },
    start() {
      if (this.timer || document.hidden || !this.items.size) return;
      const run = () => this.items.forEach((item) => item.tick());
      run();
      const delay = 1000 - (Date.now() % 1000);
      this.timer = setTimeout(() => {
        run();
        this.timer = setInterval(run, 1000);
      }, delay);
    },
    stop() {
      clearTimeout(this.timer);
      clearInterval(this.timer);
      this.timer = null;
    },
  };
  document.addEventListener('visibilitychange', () => (document.hidden ? ticker.stop() : ticker.start()));

  class ElexaCountdown extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'deadline';
      this.expire = this.dataset.expire || 'zero';
      this.showDays = this.dataset.showDays !== 'false';
      this.end = this.mode === 'daily' ? Elexa.time.nextDaily(this.dataset.cutoff) : Elexa.time.parseDeadline(this.dataset.end);
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach((el) => {
        this.units[el.dataset.unit] = el.querySelector('[data-ex-value]');
      });
      if (!this.units.days) this.showDays = false;
      this.lastLabelMinute = null;
      ticker.add(this);
    }

    disconnectedCallback() {
      ticker.remove(this);
    }

    tick() {
      let remaining = this.end - Date.now();
      if (remaining <= 0 && this.mode === 'daily') {
        this.end = Elexa.time.nextDaily(this.dataset.cutoff);
        remaining = this.end - Date.now();
      }
      if (remaining <= 0) {
        this.finish();
        return;
      }
      this.render(remaining);
    }

    render(remaining) {
      const p = Elexa.countdown.split(remaining, this.showDays);
      if (this.units.days) this.units.days.textContent = pad2(p.days);
      if (this.units.hours) this.units.hours.textContent = pad2(p.hours);
      if (this.units.minutes) this.units.minutes.textContent = pad2(p.minutes);
      if (this.units.seconds) this.units.seconds.textContent = pad2(p.seconds);
      const minute = Math.floor(remaining / 60000);
      if (minute !== this.lastLabelMinute) {
        this.lastLabelMinute = minute;
        this.setAttribute('aria-label', Elexa.countdown.label(remaining));
      }
    }

    finish() {
      ticker.remove(this);
      this.render(0);
      this.classList.add('is-expired');
      if (this.expire === 'hide') {
        this.hidden = true;
        const target = this.dataset.hideClosest && this.closest(this.dataset.hideClosest);
        if (target) target.hidden = true;
      } else if (this.expire === 'message') {
        const units = this.querySelector('.ex-countdown__units');
        const message = this.querySelector('[data-ex-cd-message]');
        if (units) units.hidden = true;
        if (message) message.hidden = false;
      }
      if (!this.expiredFired) {
        this.expiredFired = true;
        Elexa.emit(Elexa.EVENTS.countdownExpired, { element: this }, this);
      }
    }
  }
  if (!customElements.get('elexa-countdown')) customElements.define('elexa-countdown', ElexaCountdown);

  /* ------------------------------------------------------------------ *
   * Delivery dates (cut-off, business days, holidays, store timezone)
   * ------------------------------------------------------------------ */
  const DAY = 86400000;
  const ymd = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

  Elexa.delivery = {
    /** Merged options: Theme settings (Elexa.config.settings.delivery) overridden by `opts`. */
    options(opts = {}) {
      const base = (config.settings && config.settings.delivery) || {};
      return Object.assign(
        {
          cutoff: '14:00',
          processingDays: 0,
          dispatchSaturday: false,
          dispatchSunday: false,
          deliverSaturday: false,
          deliverSunday: false,
          minDays: 3,
          maxDays: 5,
          holidays: [],
          format: 'weekday_day_month',
        },
        base,
        opts
      );
    },

    /**
     * Compute the estimate. Dates are Date objects whose UTC fields are the store calendar date.
     * Returns { dispatch, earliest, latest, cutoffAt (epoch ms of today's cut-off or null), timeLeftMs }.
     */
    estimate(opts = {}) {
      const o = Elexa.delivery.options(opts);
      const holidays = new Set((o.holidays || []).map((h) => String(h).trim()));
      const isDispatch = (d) => {
        const wd = d.getUTCDay();
        if (wd === 6 && !o.dispatchSaturday) return false;
        if (wd === 0 && !o.dispatchSunday) return false;
        return !holidays.has(ymd(d));
      };
      const isDelivery = (d) => {
        const wd = d.getUTCDay();
        if (wd === 6 && !o.deliverSaturday) return false;
        if (wd === 0 && !o.deliverSunday) return false;
        return !holidays.has(ymd(d));
      };
      const p = Elexa.time.storeParts(opts.now || Date.now());
      const today = new Date(Date.UTC(p.year, p.month - 1, p.day));
      const cut = String(o.cutoff || '14:00').match(/^(\d{1,2}):(\d{2})$/) || [0, 14, 0];
      const cutMinutes = +cut[1] * 60 + +cut[2];
      const nowMinutes = p.hours * 60 + p.minutes;

      let d = new Date(today);
      const pastCutoff = nowMinutes >= cutMinutes;
      if (pastCutoff) d = new Date(d.getTime() + DAY);
      for (let i = 0; i < 60 && !isDispatch(d); i++) d = new Date(d.getTime() + DAY);
      for (let n = 0; n < (o.processingDays || 0); n++) {
        d = new Date(d.getTime() + DAY);
        for (let i = 0; i < 60 && !isDispatch(d); i++) d = new Date(d.getTime() + DAY);
      }
      let lo = Math.max(0, o.minDays | 0);
      let hi = Math.max(0, o.maxDays | 0);
      if (hi < lo) [lo, hi] = [hi, lo];
      const addDelivery = (from, count) => {
        let x = new Date(from);
        let c = 0;
        for (let i = 0; i < 60 && c < count; i++) {
          x = new Date(x.getTime() + DAY);
          if (isDelivery(x)) c++;
        }
        return x;
      };
      const dispatchIsToday = ymd(d) === ymd(today);
      const cutoffAt =
        dispatchIsToday && !pastCutoff ? Elexa.time.fromStore(p.year, p.month, p.day, +cut[1], +cut[2]) : null;
      return {
        dispatch: d,
        earliest: addDelivery(d, lo),
        latest: addDelivery(d, hi),
        sameDay: lo === hi,
        cutoffAt,
        timeLeftMs: cutoffAt ? cutoffAt - Date.now() : 0,
      };
    },

    /** Locale-aware date text. format: weekday_day_month | day_month | weekday | strftime */
    format(date, format = 'weekday_day_month', locale = config.locale) {
      if (format === 'strftime') return Elexa.delivery.strftime(date, (config.settings || {}).shipNoticeDateFormat);
      const opts = { timeZone: 'UTC' };
      if (format === 'weekday') opts.weekday = 'long';
      else if (format === 'day_month') Object.assign(opts, { day: 'numeric', month: 'short' });
      else Object.assign(opts, { weekday: 'short', day: 'numeric', month: 'short' });
      try {
        return new Intl.DateTimeFormat(locale, opts).format(date);
      } catch (e) {
        return new Intl.DateTimeFormat(undefined, opts).format(date);
      }
    },

    /**
     * Shopify-style date codes (%d %-d %e %m %-m %y %Y %B %b %A %a %j %%). Blank format gives
     * "October 12th" (English ordinal; other locales get "12 October" style from Intl).
     */
    strftime(date, fmt, locale = config.locale) {
      const lang = String(locale || 'en').slice(0, 2);
      const intl = (o) => new Intl.DateTimeFormat(locale, Object.assign({ timeZone: 'UTC' }, o)).format(date);
      if (!fmt) {
        if (lang !== 'en') return intl({ day: 'numeric', month: 'long' });
        const d = date.getUTCDate();
        const suffix = d % 10 === 1 && d !== 11 ? 'st' : d % 10 === 2 && d !== 12 ? 'nd' : d % 10 === 3 && d !== 13 ? 'rd' : 'th';
        return `${intl({ month: 'long' })} ${d}${suffix}`;
      }
      const start = Date.UTC(date.getUTCFullYear(), 0, 1);
      const map = {
        d: pad2(date.getUTCDate()),
        '-d': String(date.getUTCDate()),
        e: String(date.getUTCDate()).padStart(2, ' '),
        m: pad2(date.getUTCMonth() + 1),
        '-m': String(date.getUTCMonth() + 1),
        y: String(date.getUTCFullYear()).slice(-2),
        Y: String(date.getUTCFullYear()),
        B: intl({ month: 'long' }),
        b: intl({ month: 'short' }),
        A: intl({ weekday: 'long' }),
        a: intl({ weekday: 'short' }),
        j: String(Math.floor((date.getTime() - start) / DAY) + 1).padStart(3, '0'),
        '%': '%',
      };
      return String(fmt).replace(/%(-?[a-zA-Z%])/g, (m, code) => (code in map ? map[code] : m));
    },

    /** Range text "Thu, 10 Oct – Mon, 14 Oct" (locale-aware when Intl supports formatRange). */
    formatRange(start, end, format, locale = config.locale) {
      if (ymd(start) === ymd(end)) return Elexa.delivery.format(start, format, locale);
      return `${Elexa.delivery.format(start, format, locale)} – ${Elexa.delivery.format(end, format, locale)}`;
    },

    /** Fill [start_date] [end_date] [dispatch_date] [time_left] tokens. */
    render(template, opts = {}) {
      const est = Elexa.delivery.estimate(opts);
      const o = Elexa.delivery.options(opts);
      const wrap = opts.wrap !== false;
      const w = (text, cls = 'ex-delivery__date') => (wrap && text ? `<strong class="${cls}">${text}</strong>` : text);
      let text = String(template || '');
      if (est.sameDay) text = text.replace(/\[start_date\]\s*[–-]\s*\[end_date\]/, '[start_date]');
      let left = '';
      if (est.timeLeftMs > 0) {
        const mins = Math.ceil(est.timeLeftMs / 60000);
        left = Elexa.format(strings.deliveryTimeLeft || '[hours]h [minutes]m', {
          hours: Math.floor(mins / 60),
          minutes: mins % 60,
        });
      }
      return {
        html: text
          .replace(/\[start_date\]/g, w(Elexa.delivery.format(est.earliest, o.format)))
          .replace(/\[end_date\]/g, w(Elexa.delivery.format(est.latest, o.format)))
          .replace(/\[dispatch_date\]/g, w(Elexa.delivery.format(est.dispatch, o.format)))
          .replace(/\[time_left\]/g, w(left, 'ex-delivery__time')),
        estimate: est,
      };
    },
  };

  class ElexaDeliveryDate extends HTMLElement {
    connectedCallback() {
      this.update();
      this.interval = setInterval(() => this.update(), 60000);
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      clearTimeout(this.cutoffTimer);
    }
    update() {
      const template = this.dataset.exTemplate;
      if (!template) return;
      const result = Elexa.delivery.render(template, {
        format: this.dataset.format || undefined,
        wrap: this.dataset.wrap !== 'false',
      });
      this.innerHTML = result.html;
      clearTimeout(this.cutoffTimer);
      if (result.estimate.cutoffAt) {
        const wait = result.estimate.cutoffAt - Date.now() + 500;
        if (wait > 0 && wait < 2 * DAY) this.cutoffTimer = setTimeout(() => this.update(), wait);
      }
    }
  }
  if (!customElements.get('elexa-delivery-date')) customElements.define('elexa-delivery-date', ElexaDeliveryDate);

  /* ------------------------------------------------------------------ *
   * Carousel: <elexa-carousel> wrapping [data-ex-track] (native scroll-snap)
   *
   * Attributes: loop, autoplay="5000", step="slide|page", dots-max="10", speed="500" (ms)
   * Children:   [data-ex-track] (direct children = slides), [data-ex-prev], [data-ex-next],
   *             [data-ex-dots], [data-ex-counter], [data-ex-progress-fill]
   * Arrows, dots and autoplay glide with a JS-eased scroll like Elixir's sliders (default 500 ms
   * `ease`; per carousel: speed attribute, or CSS --ex-carousel-speed / --ex-carousel-ease).
   * Swipe, trackpad and keyboard stay native scroll-snap.
   * ------------------------------------------------------------------ */
  class ElexaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ex-track]');
      if (!this.track || this.initialised) return;
      this.initialised = true;
      this.loop = this.hasAttribute('loop');
      this.step = this.getAttribute('step') || 'slide';
      this.dotsMax = parseInt(this.getAttribute('dots-max') || '10', 10);
      this.autoplayMs = parseInt(this.getAttribute('autoplay') || '0', 10);
      this.prevButtons = this.ownElements('[data-ex-prev]');
      this.nextButtons = this.ownElements('[data-ex-next]');
      this.dotsEl = this.ownElements('[data-ex-dots]')[0];
      this.counterEl = this.ownElements('[data-ex-counter]')[0];
      this.progressFill = this.ownElements('[data-ex-progress-fill]')[0];
      this.lastIndex = -1;

      this.onPrev = (e) => {
        e.preventDefault();
        this.prev();
        this.restartAutoplay();
      };
      this.onNext = (e) => {
        e.preventDefault();
        this.next();
        this.restartAutoplay();
      };
      this.prevButtons.forEach((b) => b.addEventListener('click', this.onPrev));
      this.nextButtons.forEach((b) => b.addEventListener('click', this.onNext));

      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // A finger, wheel or pointer on the track takes over from a running glide.
      this.onUserScroll = () => {
        if (this.glideRaf) this.endGlide();
      };
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
        this.track.addEventListener(type, this.onUserScroll, { passive: true })
      );
      this.resizeObserver = new ResizeObserver(() => {
        this.buildDots();
        this.update();
      });
      this.resizeObserver.observe(this.track);

      this.addEventListener('shopify:block:select', (e) => {
        const slide = this.slides().find((s) => s === e.target || s.contains(e.target));
        if (slide) this.goTo(this.slides().indexOf(slide), true);
        this.paused = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.paused = false;
      });

      this.buildDots();
      this.update();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.glideRaf) this.endGlide();
      clearInterval(this.autoplayTimer);
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
      if (this.io) this.io.disconnect();
      this.initialised = false;
    }

    /** Elements inside this carousel but not inside a nested carousel. */
    ownElements(selector) {
      return Array.from(this.querySelectorAll(selector)).filter((el) => el.closest('elexa-carousel') === this);
    }

    slides() {
      return Array.from(this.track.children).filter(
        (el) => !el.hasAttribute('hidden') && !el.hasAttribute('data-ex-clone') && el.getClientRects().length
      );
    }

    get rtl() {
      return Elexa.isRTL(this.track);
    }

    get position() {
      return Math.abs(this.track.scrollLeft);
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /** Slide width + gap (sub-pixel, so glides land exactly on the snap points). */
    slideStep() {
      const slides = this.slides();
      if (slides.length > 1) {
        const step = Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left);
        return step || slides[0].offsetWidth;
      }
      return slides[0] ? slides[0].getBoundingClientRect().width : this.track.clientWidth;
    }

    perView() {
      const step = this.slideStep();
      return step ? Math.max(1, Math.round((this.track.clientWidth + 1) / step)) : 1;
    }

    stops() {
      const slides = this.slides().length;
      if (!slides || this.maxScroll <= 1) return 1;
      return Math.max(1, Math.min(slides, Math.ceil(this.maxScroll / this.slideStep() - 0.01) + 1));
    }

    /** Glide duration in ms: speed attribute, else --ex-carousel-speed, else 500. */
    speed() {
      const attr = this.getAttribute('speed');
      if (attr !== null && attr !== '') return Math.max(0, Elexa.toMs(attr, 500));
      return Math.max(0, Elexa.toMs(getComputedStyle(this).getPropertyValue('--ex-carousel-speed'), 500));
    }

    scrollToPosition(pos, instant = false) {
      const left = Math.max(0, Math.min(pos, this.maxScroll));
      if (this.glideRaf) this.endGlide();
      const duration = instant || Elexa.reducedMotion() ? 0 : this.speed();
      if (!duration || Math.abs(left - this.position) < 1) {
        this.track.scrollTo({ left: this.rtl ? -left : left, behavior: 'instant' });
        return;
      }
      this.glide(left, duration);
    }

    /** JS-eased scroll to `target` (snap is paused for the glide and restored at the end). */
    glide(target, duration) {
      const track = this.track;
      const from = this.position;
      const distance = target - from;
      const sign = this.rtl ? -1 : 1;
      const ease = Elexa.easing(getComputedStyle(this).getPropertyValue('--ex-carousel-ease') || 'ease');
      this.glideTarget = target;
      this.savedSnap = track.style.scrollSnapType;
      this.savedBehavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      const start = performance.now();
      const frame = (now) => {
        const p = Math.min(1, (now - start) / duration);
        track.scrollLeft = sign * (from + distance * ease(p));
        if (p < 1) this.glideRaf = requestAnimationFrame(frame);
        else this.endGlide();
      };
      this.glideRaf = requestAnimationFrame(frame);
    }

    endGlide() {
      cancelAnimationFrame(this.glideRaf);
      this.glideRaf = null;
      this.glideTarget = null;
      this.track.style.scrollSnapType = this.savedSnap || '';
      this.track.style.scrollBehavior = this.savedBehavior || '';
    }

    /** Where the carousel is heading (a running glide's target) or where it is. */
    get destination() {
      return this.glideRaf && this.glideTarget !== null ? this.glideTarget : this.position;
    }

    amount() {
      const step = this.slideStep();
      if (this.step === 'page') return Math.max(step, Math.floor((this.track.clientWidth + 1) / step) * step);
      return step;
    }

    atStart() {
      return this.position <= 2;
    }

    atEnd() {
      return this.position >= this.maxScroll - 2;
    }

    next() {
      const base = this.destination;
      if (base >= this.maxScroll - 2) {
        if (this.loop) this.scrollToPosition(0);
        return;
      }
      const target = Math.round((base + this.amount()) / this.slideStep()) * this.slideStep();
      this.scrollToPosition(target);
    }

    prev() {
      const base = this.destination;
      if (base <= 2) {
        if (this.loop) this.scrollToPosition(this.maxScroll);
        return;
      }
      const target = Math.round((base - this.amount()) / this.slideStep()) * this.slideStep();
      this.scrollToPosition(target);
    }

    goTo(index, instant = false) {
      this.scrollToPosition(index * this.slideStep(), instant);
    }

    currentIndex() {
      const total = this.slides().length;
      if (!total) return 0;
      if (this.atEnd() && this.maxScroll > 1) return this.stops() - 1;
      return Math.max(0, Math.min(total - 1, Math.round(this.position / this.slideStep())));
    }

    buildDots() {
      if (!this.dotsEl) return;
      const stops = this.stops();
      if (stops === this.dotCount) return;
      this.dotCount = stops;
      this.dotsEl.innerHTML = '';
      const tooMany = stops > this.dotsMax;
      this.dotsEl.hidden = tooMany || stops <= 1;
      if (this.counterEl && this.counterEl.classList.contains('ex-counter--fallback')) this.counterEl.hidden = !tooMany;
      if (tooMany || stops <= 1) return;
      for (let i = 0; i < stops; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ex-dot';
        dot.setAttribute('aria-label', Elexa.format(strings.goToSlide || 'Go to slide [index]', { index: i + 1 }));
        if (this.track.id) dot.setAttribute('aria-controls', this.track.id);
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.restartAutoplay();
        });
        this.dotsEl.appendChild(dot);
      }
    }

    update() {
      const max = this.maxScroll;
      const scrollable = max > 1;
      this.toggleAttribute('static', !scrollable);
      const atStart = this.atStart();
      const atEnd = this.atEnd();
      this.prevButtons.forEach((b) => (b.disabled = !scrollable || (!this.loop && atStart)));
      this.nextButtons.forEach((b) => (b.disabled = !scrollable || (!this.loop && atEnd)));

      if (this.progressFill) {
        const ratio = scrollable ? Math.min(1, Math.max(0, this.position / max)) : 1;
        this.progressFill.style.setProperty('--ex-progress', ratio.toFixed(4));
      }

      const index = this.currentIndex();
      const total = this.slides().length;
      if (this.counterEl) {
        const current = atEnd && scrollable ? total : Math.min(total, index + 1);
        const cur = this.counterEl.querySelector('[data-ex-counter-current]');
        const tot = this.counterEl.querySelector('[data-ex-counter-total]');
        const label = this.counterEl.querySelector('[data-ex-counter-label]');
        if (cur) cur.textContent = current;
        if (tot) tot.textContent = total;
        if (label) label.textContent = Elexa.format(strings.counter || '[current] of [total]', { current, total });
      }
      if (this.dotsEl) {
        Array.from(this.dotsEl.children).forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }
      if (index !== this.lastIndex) {
        this.lastIndex = index;
        Elexa.emit(Elexa.EVENTS.carouselChange, { index, total }, this);
      }
    }

    setupAutoplay() {
      if (!this.autoplayMs || Elexa.reducedMotion() || Elexa.isDesignMode()) return;
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.addEventListener('mouseenter', () => (this.hovered = true));
      this.addEventListener('mouseleave', () => (this.hovered = false));
      this.addEventListener('focusin', () => (this.focused = true));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.focused = false;
      });
      this.io = new IntersectionObserver((entries) => entries.forEach((en) => (this.visible = en.isIntersecting)));
      this.io.observe(this);
      this.restartAutoplay();
    }

    restartAutoplay() {
      if (!this.autoplayMs || Elexa.reducedMotion() || Elexa.isDesignMode()) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = setInterval(() => {
        if (this.paused || this.hovered || this.focused || !this.visible || document.hidden) return;
        if (this.atEnd()) this.scrollToPosition(0);
        else this.next();
      }, this.autoplayMs);
    }
  }
  if (!customElements.get('elexa-carousel')) customElements.define('elexa-carousel', ElexaCarousel);

  /* ------------------------------------------------------------------ *
   * Accordion: <elexa-accordion single open-first> wrapping <details> items
   * ------------------------------------------------------------------ */
  /*
   * Motion measured on Elixir: answers open on max-height (no fade). Timing comes from CSS so each
   * accordion family can differ: --ex-acc-duration (300ms), --ex-acc-easing (ease) and
   * --ex-acc-floor (300px: the max-height opens to at least this, which is why short answers open
   * fast and close after a short pause, exactly like Elixir's FAQ). Product collapsible rows use
   * 500ms cubic-bezier(0.4, 0, 0.2, 1) with no floor (set in elexa-base.css).
   */
  class ElexaAccordion extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.single = this.hasAttribute('single');
      this.addEventListener('click', (e) => {
        const summary = e.target.closest('summary');
        if (!summary || summary.closest('elexa-accordion') !== this) return;
        const details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS') return;
        e.preventDefault();
        this.toggle(details);
      });
      // Theme editor: selecting a block opens its row (no animation, nothing else changes).
      this.addEventListener('shopify:block:select', (e) => {
        const details = this.items().find((d) => d === e.target || d.contains(e.target));
        if (!details || details.open) return;
        if (this.single) this.items().forEach((d) => d !== details && this.isOpen(d) && this.close(d, true));
        this.open(details, true);
      });
      if (this.hasAttribute('open-first')) {
        const items = this.items();
        if (items.length && !items.some((d) => d.open)) items[0].open = true;
      }
      this.items().forEach((d) => this.syncAria(d));
    }

    items() {
      return Array.from(this.querySelectorAll('details')).filter((d) => d.closest('elexa-accordion') === this);
    }

    panel(details) {
      return details.querySelector(':scope > [data-ex-acc-panel]') || details.querySelector(':scope > :not(summary)');
    }

    syncAria(details) {
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', this.isOpen(details) ? 'true' : 'false');
    }

    /** While a row animates closed it stays [open]; data-ex-closing lets icons turn back at once. */
    setClosing(details, closing) {
      details.exClosing = closing;
      details.toggleAttribute('data-ex-closing', closing);
    }

    timing() {
      const cs = getComputedStyle(this);
      return {
        duration: Elexa.toMs(cs.getPropertyValue('--ex-acc-duration'), 300),
        easing: (cs.getPropertyValue('--ex-acc-easing') || '').trim() || 'ease',
        floor: parseFloat(cs.getPropertyValue('--ex-acc-floor')) || 0,
      };
    }

    /** Stop a running open/close; returns the max-height it had reached (px) or null. */
    stop(panel) {
      const anim = panel && panel.exAnim;
      if (!anim) return null;
      const current = parseFloat(getComputedStyle(panel).maxHeight);
      anim.onfinish = null;
      anim.cancel();
      panel.exAnim = null;
      return Number.isFinite(current) ? current : null;
    }

    animatePanel(panel, from, to, done) {
      const { duration, easing } = this.timing();
      panel.style.overflow = 'hidden';
      const anim = panel.animate([{ maxHeight: `${from}px` }, { maxHeight: `${to}px` }], { duration, easing });
      panel.exAnim = anim;
      anim.onfinish = () => {
        panel.exAnim = null;
        panel.style.overflow = '';
        if (done) done();
      };
    }

    isOpen(details) {
      return details.open && !details.exClosing;
    }

    toggle(details, force) {
      const open = typeof force === 'boolean' ? force : !this.isOpen(details);
      if (open && this.single) this.items().forEach((d) => d !== details && this.isOpen(d) && this.close(d));
      if (open) this.open(details);
      else this.close(details);
    }

    open(details, instant = false) {
      if (this.isOpen(details)) return;
      const panel = this.panel(details);
      const reached = this.stop(panel);
      this.setClosing(details, false);
      details.open = true;
      this.syncAria(details);
      Elexa.emit('elexa:accordion-toggle', { details, open: true }, this);
      if (!panel || instant || Elexa.reducedMotion() || !panel.animate) {
        if (panel) panel.style.overflow = '';
        return;
      }
      const { floor } = this.timing();
      this.animatePanel(panel, reached || 0, Math.max(panel.scrollHeight, floor));
    }

    close(details, instant = false) {
      if (!this.isOpen(details)) return;
      const panel = this.panel(details);
      const reached = this.stop(panel);
      const done = () => {
        this.setClosing(details, false);
        details.open = false;
        this.syncAria(details);
        if (panel) panel.style.overflow = '';
      };
      this.setClosing(details, true);
      this.syncAria(details);
      Elexa.emit('elexa:accordion-toggle', { details, open: false }, this);
      if (!panel || instant || Elexa.reducedMotion() || !panel.animate) return done();
      const { floor } = this.timing();
      const from = reached !== null ? reached : Math.max(panel.scrollHeight, floor);
      this.animatePanel(panel, from, 0, done);
    }
  }
  if (!customElements.get('elexa-accordion')) customElements.define('elexa-accordion', ElexaAccordion);

  /* ------------------------------------------------------------------ *
   * Cart pipeline (stub owned by the foundation, extended by group B)
   *
   * Every cart write from any group goes through Elexa.cart so the drawer re-render,
   * Dawn's PUB_SUB_EVENTS.cartUpdate, `elexa:cart-updated` and group B's hooks always run.
   * Mutations are queued FIFO.
   * ------------------------------------------------------------------ */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const cartUrl = (path) => `${routes[path] || ''}`;

  class CartError extends Error {
    constructor(message, data) {
      super(message);
      this.name = 'ElexaCartError';
      this.data = data;
    }
  }

  const cart = {
    /**
     * Extension points (group B):
     *  - hooks.beforeRequest: fn({ action, body }) may mutate body; awaited
     *  - hooks.afterChange:   fn({ action, cart, response, source, options }) awaited after each
     *                         successful render, in order (gift reconciliation lives here).
     *                         options.reconcile === false marks a reconciliation follow-up call.
     *  - hooks.afterRender:   fn({ action, sections, cart }) after DOM replacement
     *  - sections: extra section ids to request on every mutation
     *  - renderers: { sectionId: (html, ctx) => void } overrides for custom drawers
     */
    hooks: { beforeRequest: [], afterChange: [], afterRender: [] },
    sections: [],
    renderers: {},
    queue: Promise.resolve(),
    seq: 0,
    renderedSeq: 0,
    lastCart: null,
    CartError,

    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    },

    /** Section ids to request with each mutation, based on the cart UI on this page. */
    sectionIds() {
      const ids = [];
      if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
      if (document.querySelector('cart-notification')) ids.push('cart-notification-product', 'cart-notification-button');
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      if (items && items.dataset.id) ids.push(items.dataset.id);
      if (footer && footer.dataset.id) ids.push(footer.dataset.id);
      if (items) ids.push('cart-live-region-text');
      ids.push('cart-icon-bubble');
      this.sections.forEach((id) => ids.push(id));
      return Array.from(new Set(ids)).slice(0, 5);
    },

    async request(url, body, method = 'POST') {
      const options = { method, headers: { Accept: 'application/json' } };
      if (method !== 'GET') {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body || {});
      }
      const res = await fetch(url, options);
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
      if (!res.ok || (data && data.status && data.status >= 400 && data.description)) {
        const message = (data && (data.description || data.message)) || strings.cartError || 'Cart error';
        throw new CartError(message, data);
      }
      return data;
    },

    async runHooks(name, payload) {
      for (const fn of this.hooks[name] || []) {
        try {
          await fn(payload);
        } catch (e) {
          console.error(`[Elexa.cart] ${name} hook failed`, e);
        }
      }
    },

    /** GET /cart.js */
    get() {
      return this.request(`${cartUrl('cart')}.js`, null, 'GET').then((c) => (this.lastCart = c));
    },

    /** Replace cart UI from a Section Rendering payload { sectionId: html }. */
    render(sections, ctx = {}) {
      if (!sections) return;
      const seq = ctx.seq || ++this.seq;
      if (seq < this.renderedSeq) return;
      this.renderedSeq = seq;
      const cartData = ctx.cart;
      const drawer = document.querySelector('cart-drawer');
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        if (this.renderers[id]) return this.renderers[id](html, ctx);
        const doc = parseHTML(html);
        if (id === 'cart-drawer' && drawer) {
          const target = document.getElementById('CartDrawer');
          const source = doc.querySelector('#CartDrawer');
          if (target && source) {
            target.innerHTML = source.innerHTML;
            const overlay = target.querySelector('#CartDrawer-Overlay');
            if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
          }
          return;
        }
        if (id === 'cart-notification-product') {
          const key = ctx.key;
          const target = document.getElementById('cart-notification-product');
          const source = key ? doc.querySelector(`[id="cart-notification-product-${key}"]`) : null;
          if (target && source) target.innerHTML = source.innerHTML;
          return;
        }
        const items = document.getElementById('main-cart-items');
        const footer = document.getElementById('main-cart-footer');
        if (items && id === items.dataset.id) {
          const target = items.querySelector('.js-contents');
          const source = doc.querySelector('.js-contents');
          if (target && source) target.innerHTML = source.innerHTML;
          return;
        }
        if (footer && id === footer.dataset.id) {
          const target = footer.querySelector('.js-contents');
          const source = doc.querySelector('.js-contents');
          if (target && source) target.innerHTML = source.innerHTML;
          return;
        }
        const target = document.getElementById(id);
        const source = doc.querySelector('.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      });
      if (cartData) {
        const empty = cartData.item_count === 0;
        if (drawer) {
          drawer.classList.toggle('is-empty', empty);
          const inner = drawer.querySelector('.drawer__inner');
          if (inner && !empty) inner.classList.remove('is-empty');
        }
        const cartItems = document.querySelector('cart-items');
        if (cartItems) cartItems.classList.toggle('is-empty', empty);
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', empty);
      }
      this.runHooks('afterRender', { action: ctx.action, sections, cart: cartData });
    },

    /** Open the cart UI: drawer, else notification, else go to /cart. */
    open(trigger) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        if (trigger && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
        drawer.open(trigger);
        return;
      }
      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.open === 'function') {
        notification.open();
        return;
      }
      if (!document.getElementById('main-cart-items')) window.location.href = cartUrl('cart');
    },

    announce(action, cartData, source, response, options = {}) {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: `elexa:${source}`, cartData, elexa: true });
      } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: `elexa:${source}`, cartData, elexa: true });
      }
      Elexa.emit(Elexa.EVENTS.cartUpdated, { cart: cartData, source, action, response, options });
    },

    fail(error, source, action, options = {}) {
      const message = error && error.message ? error.message : strings.cartError;
      const errorBox = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (errorBox && ['change', 'update'].includes(action)) errorBox.textContent = message;
      // An add started from a specific element (options.trigger) shows its message next to that element,
      // unless the caller handles errors itself (options.showError === false). The main product form passes
      // no trigger and keeps its own error flow.
      const trigger = options.trigger instanceof Element ? options.trigger : null;
      if (trigger && options.showError !== false) this.showInlineError(message, trigger);
      const pub = typeof window.publish === 'function' ? window.publish : typeof publish === 'function' ? publish : null;
      const events = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
      if (pub && events) pub(events.cartError, { source: `elexa:${source}`, errors: message, message });
      Elexa.emit(Elexa.EVENTS.cartError, { message, source, action, data: error && error.data, trigger });
      throw error;
    },

    /**
     * Show a cart error next to the element that started the request.
     * Uses the nearest [data-ex-cart-error] inside the trigger's [data-ex-error-scope] (or its parent),
     * otherwise inserts <p class="ex-cart-error" role="alert"> right after the trigger. Cleared after 6 s,
     * on the next successful add from the same trigger, or with showInlineError('', trigger).
     * (Elexa.cart.showError is group B's drawer-error helper, so this has its own name.)
     */
    showInlineError(message, trigger) {
      if (!(trigger instanceof Element) || !trigger.isConnected) return null;
      const scope = trigger.closest('[data-ex-error-scope]') || trigger.parentElement;
      let box = trigger.exErrorBox && trigger.exErrorBox.isConnected ? trigger.exErrorBox : null;
      if (!box && scope) box = scope.querySelector('[data-ex-cart-error]');
      if (!box) {
        if (!message) return null;
        box = document.createElement('p');
        box.className = 'ex-cart-error';
        box.setAttribute('data-ex-cart-error', '');
        box.setAttribute('role', 'alert');
        trigger.insertAdjacentElement('afterend', box);
        // Follow the button's alignment (centred CTAs get a centred message).
        const pcs = box.parentElement ? getComputedStyle(box.parentElement) : null;
        if (pcs && (pcs.textAlign === 'center' || (pcs.display.includes('flex') && (pcs.alignItems === 'center' || pcs.justifyContent === 'center')))) {
          box.classList.add('ex-cart-error--center');
        }
      }
      trigger.exErrorBox = box;
      clearTimeout(box.exTimer);
      box.textContent = message || '';
      box.hidden = !message;
      if (message) {
        box.exTimer = setTimeout(() => {
          box.textContent = '';
          box.hidden = true;
        }, 6000);
      }
      return box;
    },

    normaliseItems(items) {
      const list = Array.isArray(items) ? items : [items];
      return list
        .filter((i) => i && (i.id || i.variant_id))
        .map((i) => {
          const item = { id: Number(i.id || i.variant_id), quantity: Math.max(1, parseInt(i.quantity || 1, 10)) };
          if (i.properties && Object.keys(i.properties).length) item.properties = i.properties;
          if (i.selling_plan) item.selling_plan = Number(i.selling_plan);
          return item;
        });
    },

    /**
     * Add items: [{ id, quantity, properties, selling_plan }].
     * options: { open = true, source, reconcile, trigger (Element: show errors next to it), showError (false = caller shows them) }
     * Resolves { cart, items } or rejects with an ElexaCartError (Shopify's description).
     */
    add(items, options = {}) {
      const { open = true, source = 'elexa' } = options;
      return this.enqueue(async () => {
        const seq = ++this.seq;
        try {
          const body = { items: this.normaliseItems(items), sections: this.sectionIds().join(','), sections_url: window.location.pathname };
          if (!body.items.length) throw new CartError(strings.cartError || 'No items');
          await this.runHooks('beforeRequest', { action: 'add', body, options });
          const response = await this.request(`${cartUrl('cartAdd')}.js`, body);
          const cartData = await this.get();
          const added = response.items || [response];
          this.render(response.sections, { seq, cart: cartData, action: 'add', key: added[0] && added[0].key });
          if (open) {
            const notification = document.querySelector('cart-notification');
            if (notification && !document.querySelector('cart-drawer') && typeof notification.open === 'function') notification.open();
            else this.open();
          }
          await this.runHooks('afterChange', { action: 'add', cart: cartData, response, source, options });
          this.announce('add', cartData, source, response, options);
          if (options.trigger instanceof Element && options.trigger.exErrorBox) this.showInlineError('', options.trigger);
          return { cart: cartData, items: added };
        } catch (e) {
          return this.fail(e, source, 'add', options);
        }
      });
    },

    /** Change a line by its key. options: { source, open = false, properties, selling_plan, reconcile } */
    change(lineKey, quantity, options = {}) {
      const { source = 'elexa', open = false } = options;
      return this.enqueue(async () => {
        const seq = ++this.seq;
        try {
          const body = { id: String(lineKey), sections: this.sectionIds().join(','), sections_url: window.location.pathname };
          if (quantity !== undefined && quantity !== null) body.quantity = Math.max(0, parseInt(quantity, 10) || 0);
          if (options.properties) body.properties = options.properties;
          if (options.selling_plan !== undefined) body.selling_plan = options.selling_plan || null;
          await this.runHooks('beforeRequest', { action: 'change', body, options });
          const response = await this.request(`${cartUrl('cartChange')}.js`, body);
          this.lastCart = response;
          this.render(response.sections, { seq, cart: response, action: 'change' });
          if (open) this.open();
          await this.runHooks('afterChange', { action: 'change', cart: response, response, source, options });
          this.announce('change', response, source, response, options);
          return { cart: response };
        } catch (e) {
          return this.fail(e, source, 'change');
        }
      });
    },

    /** /cart/update.js with any body: { updates, note, attributes, discount }. */
    update(payload = {}, options = {}) {
      const { source = 'elexa', open = false } = options;
      return this.enqueue(async () => {
        const seq = ++this.seq;
        try {
          const body = Object.assign({}, payload, { sections: this.sectionIds().join(','), sections_url: window.location.pathname });
          await this.runHooks('beforeRequest', { action: 'update', body, options });
          const response = await this.request(`${cartUrl('cartUpdate')}.js`, body);
          this.lastCart = response;
          this.render(response.sections, { seq, cart: response, action: 'update' });
          if (open) this.open();
          await this.runHooks('afterChange', { action: 'update', cart: response, response, source, options });
          this.announce('update', response, source, response, options);
          return { cart: response };
        } catch (e) {
          return this.fail(e, source, 'update');
        }
      });
    },

    /**
     * Re-render the cart UI. options: { open = false, sections (pre-fetched payload), source }
     * Without a payload, fetches /cart.js plus the cart sections.
     */
    refresh(options = {}) {
      const { open = false, source = 'elexa' } = options;
      return this.enqueue(async () => {
        const seq = ++this.seq;
        try {
          const ids = this.sectionIds();
          let sections = options.sections || null;
          const cartData = await this.get();
          if (!sections) {
            const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`;
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            sections = res.ok ? await res.json() : null;
          }
          this.render(sections, { seq, cart: cartData, action: 'refresh' });
          if (open) this.open();
          await this.runHooks('afterChange', { action: 'refresh', cart: cartData, response: null, source, options });
          this.announce('refresh', cartData, source, null, options);
          return { cart: cartData };
        } catch (e) {
          return this.fail(e, source, 'refresh');
        }
      });
    },

    /**
     * Apply a discount code (keeps codes already on the cart). Resolves { cart, code, applied }.
     * `applied` is false when Shopify accepted the code but it does not apply to this cart.
     */
    async applyDiscount(code, options = {}) {
      const clean = String(code || '').trim();
      if (!clean) throw new CartError('Empty discount code');
      const current = await this.get();
      const existing = (current.discount_codes || []).map((d) => d.code).filter(Boolean);
      const codes = Array.from(new Set(existing.concat(clean)));
      const result = await this.update({ discount: codes.join(',') }, Object.assign({ source: 'discount' }, options));
      const match = (result.cart.discount_codes || []).find((d) => String(d.code).toLowerCase() === clean.toLowerCase());
      return { cart: result.cart, code: clean, applied: Boolean(match && match.applicable) };
    },

    /** Remove one discount code (or all when code is blank). */
    async removeDiscount(code, options = {}) {
      const current = await this.get();
      const clean = String(code || '').trim().toLowerCase();
      const codes = clean ? (current.discount_codes || []).map((d) => d.code).filter((c) => String(c).toLowerCase() !== clean) : [];
      return this.update({ discount: codes.join(',') }, Object.assign({ source: 'discount' }, options));
    },
  };
  Elexa.cart = Object.assign(Elexa.cart || {}, cart);

  // Document-level request event: same as Elexa.cart.refresh(detail).
  document.addEventListener(Elexa.EVENTS.cartRefreshRequest, (e) => {
    Elexa.cart.refresh(Object.assign({ source: 'request' }, e.detail || {})).catch(() => {});
  });

  // Cart changes made by Dawn code (product form, cart page, quick add) still emit elexa:cart-updated
  // and run group B's afterChange hooks.
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.elexa || String(event.source || '').startsWith('elexa')) return;
      Elexa.cart
        .get()
        .then(async (cartData) => {
          await Elexa.cart.runHooks('afterChange', { action: 'external', cart: cartData, response: event.cartData, source: event.source, options: {} });
          Elexa.emit(Elexa.EVENTS.cartUpdated, { cart: cartData, source: event.source || 'dawn', action: 'external' });
        })
        .catch(() => {});
    });
  });

  /* ------------------------------------------------------------------ *
   * Global guards (theme-layout 3.6)
   * ------------------------------------------------------------------ */
  // 1. Add-to-cart guard: never post an add form without a variant id.
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      const id = form.querySelector('[name="id"]');
      if (!id || !String(id.value || '').trim()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },
    true
  );

  // 2. Quantity guard: Enter in a quantity field commits the value instead of submitting.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.matches('.quantity__input, input[name="quantity"], input[name^="updates"]')) return;
    const form = input.form;
    if (form && /\/cart\/change/.test(form.getAttribute('action') || '') && !form.querySelector('[name="id"], [name="line"]')) {
      const key = input.dataset.quantityLineKey || input.dataset.quantityVariantId;
      if (key) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.value = key;
        form.appendChild(hidden);
      }
    }
    e.preventDefault();
    input.blur();
  });

  // 3. Empty-cart checkout guard (drawer).
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('cart-drawer [name="checkout"], cart-drawer .cart__checkout-button');
      if (!btn) return;
      const drawer = btn.closest('cart-drawer');
      if (drawer && (drawer.classList.contains('is-empty') || !drawer.querySelector('.cart-item'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * Declarative actions: [data-ex-action]
   * scroll-top | scroll-first-section | scroll-main-product | add-to-cart | copy | none
   * ------------------------------------------------------------------ */
  function stickyOffset() {
    let offset = 0;
    const header = document.querySelector('.section-header, .shopify-section-header-sticky');
    if (header && ['sticky', 'fixed'].includes(getComputedStyle(header).position)) offset += header.offsetHeight;
    document.querySelectorAll('.announcement-bar-section, [data-ex-sticky-bar="top"]').forEach((bar) => {
      if (['sticky', 'fixed'].includes(getComputedStyle(bar).position)) offset += bar.offsetHeight;
    });
    return offset;
  }

  Elexa.scrollTo = (target, { pulse = false } = {}) => {
    const behavior = Elexa.reducedMotion() ? 'instant' : 'smooth';
    if (!target) return window.scrollTo({ top: 0, behavior });
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - stickyOffset() - 20);
    window.scrollTo({ top, behavior });
    if (pulse && !Elexa.reducedMotion() && target.animate) {
      setTimeout(() => target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.01)' }, { transform: 'scale(1)' }], { duration: 300 }), 400);
    }
  };

  Elexa.copy = async (text, button) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.className = 'ex-copyable';
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        ok = document.execCommand('copy');
      } catch (err) {
        ok = false;
      }
      ta.remove();
    }
    if (button) {
      button.setAttribute('data-copied', ok ? 'true' : 'false');
      clearTimeout(button.exCopyTimer);
      button.exCopyTimer = setTimeout(() => button.removeAttribute('data-copied'), 2000);
    }
    return ok;
  };

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-ex-action]');
    if (!el) return;
    const action = el.dataset.exAction;
    switch (action) {
      case 'none':
        e.preventDefault();
        break;
      case 'scroll-top':
        e.preventDefault();
        Elexa.scrollTo(null);
        break;
      case 'scroll-first-section': {
        e.preventDefault();
        const first = document.querySelector('#MainContent > .shopify-section');
        Elexa.scrollTo(first, { pulse: true });
        break;
      }
      case 'scroll-main-product': {
        e.preventDefault();
        const product =
          document.querySelector('[id^="MainProduct-"], .product-section, [data-ex-main-product]') ||
          document.getElementById('MainContent');
        Elexa.scrollTo(product);
        break;
      }
      case 'copy': {
        e.preventDefault();
        Elexa.copy(el.dataset.copyText || el.textContent.trim(), el);
        break;
      }
      case 'add-to-cart': {
        e.preventDefault();
        if (el.disabled || el.getAttribute('aria-disabled') === 'true') return;
        const id = el.dataset.variantId;
        if (!id) return;
        let properties;
        try {
          properties = el.dataset.properties ? JSON.parse(el.dataset.properties) : undefined;
        } catch (err) {
          properties = undefined;
        }
        el.classList.add('loading');
        el.setAttribute('aria-busy', 'true');
        const spinner = el.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        Elexa.cart
          .add([{ id, quantity: el.dataset.quantity || 1, properties, selling_plan: el.dataset.sellingPlan }], {
            open: el.dataset.openCart !== 'false',
            source: el.dataset.source || 'button',
            trigger: el,
          })
          .catch(() => {})
          .finally(() => {
            el.classList.remove('loading');
            el.removeAttribute('aria-busy');
            if (spinner) spinner.classList.add('hidden');
          });
        break;
      }
      default:
        break;
    }
  });

  document.documentElement.classList.add('ex-js');
})();
